import { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
} from '@mui/material';
import {
  Event as EventIcon,
  CalendarToday,
  AccessTime,
  LocationOn,
  Cancel as CancelIcon,
  CheckCircle,
  HourglassEmpty,
  Repeat,
} from '@mui/icons-material';
import { cancelRegistration, cancelVolunteerSeriesRegistrations } from '@services/registrationService';
import { getEventsBySeries } from '@services/eventService';
import { useAuth } from '@hooks/useAuth';

const ScheduleEventCard = ({ registration, onUpdate }) => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelScope, setCancelScope] = useState('single');
  const [cancelReason, setCancelReason] = useState('');
  const { currentUser } = useAuth();

  const { event, assignmentName, status } = registration;
  const isRecurringSeries = event?.isRecurring && event?.recurrenceId;

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Get status config
  const getStatusConfig = () => {
    switch (status) {
      case 'registered':
        return {
          label: 'Registered',
          color: 'success',
          icon: <CheckCircle />,
        };
      case 'attended':
        return {
          label: 'Attended',
          color: 'primary',
          icon: <CheckCircle />,
        };
      case 'cancelled':
        return {
          label: 'Cancelled',
          color: 'error',
          icon: <CancelIcon />,
        };
      default:
        return {
          label: 'Pending',
          color: 'default',
          icon: <HourglassEmpty />,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const canCancel = status === 'registered';

  const handleCancelClick = () => {
    setCancelScope('single');
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    try {
      setCancelling(true);

      if (cancelScope === 'series' && isRecurringSeries) {
        const now = new Date();
        const seriesEvents = await getEventsBySeries(event.recurrenceId, event.organizationId);
        const futureEventIds = seriesEvents
          .filter((ev) => ev.date?.toDate() >= now)
          .map((ev) => ev.eventId);
        await cancelVolunteerSeriesRegistrations(currentUser.uid, futureEventIds, assignmentName, cancelReason);
      } else {
        await cancelRegistration(registration.registrationId, cancelReason);
      }

      setCancelDialogOpen(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error cancelling registration:', error);
      alert('Failed to cancel registration. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (!event) {
    return null;
  }

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: status === 'cancelled' ? 'grey.300' : 'primary.main',
          bgcolor: status === 'cancelled' ? 'grey.50' : 'background.paper',
          opacity: status === 'cancelled' ? 0.7 : 1,
        }}
      >
        <CardContent>
          {/* Horizontal Layout */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            {/* Event Icon/Image */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: { xs: 'auto', sm: 80 },
                minHeight: { xs: 60, sm: 80 },
                bgcolor: 'primary.lighter',
                borderRadius: 2,
                p: 2,
              }}
            >
              <EventIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main' }} />
            </Box>

            {/* Event Details */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Title and Status */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {event.title}
                </Typography>
                <Chip
                  icon={statusConfig.icon}
                  label={statusConfig.label}
                  color={statusConfig.color}
                  size="small"
                  sx={{ fontWeight: 'medium' }}
                />
              </Box>

              {/* Assignment Badge */}
              <Chip
                label={assignmentName}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ mb: registration.assignmentStartTime ? 0.5 : 2 }}
              />
              {registration.assignmentStartTime && registration.assignmentEndTime && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  {formatTime(registration.assignmentStartTime)} – {formatTime(registration.assignmentEndTime)}
                </Typography>
              )}

              {/* Event Info */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Date */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(event.date)}
                  </Typography>
                </Box>

                {/* Time */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatTime(event.startTime)} - {formatTime(event.endTime)}
                  </Typography>
                </Box>

                {/* Location */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {event.location}
                  </Typography>
                </Box>
              </Box>

              {/* Cancel Button (for upcoming registered events) */}
              {canCancel && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelClick}
                  sx={{
                    mt: 2,
                    minHeight: 48,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Cancel Registration
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Registration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your registration for:
          </DialogContentText>
          <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {event.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {assignmentName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(event.date)} at {formatTime(event.startTime)}
            </Typography>
          </Box>

          {isRecurringSeries && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Repeat fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  This is a recurring event ({event.totalInstances} total).
                </Typography>
              </Box>
              <RadioGroup value={cancelScope} onChange={(e) => setCancelScope(e.target.value)}>
                <FormControlLabel value="single" control={<Radio size="small" />} label="Cancel this event only" />
                <FormControlLabel value="series" control={<Radio size="small" />} label="Cancel all upcoming events in this series" />
              </RadioGroup>
            </Box>
          )}

          <TextField
            label="Reason for cancelling (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
          <DialogContentText color="error">
            This cannot be undone. You will need to re-register if you change your mind.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            Keep Registration
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelling...' : cancelScope === 'series' ? 'Cancel All Upcoming' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduleEventCard;
